import React, { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import axiosInstance from "../api/axiosInstance";
import getApiUrl from "../utils/api";
import { usePostHog } from "posthog-js/react";
import { POSTHOG_EVENTS } from "../constants/posthog-events";
import {
  FaFilePdf,
  FaFileImage,
  FaFileWord,
  FaFileExcel,
  FaFileArchive,
  FaFileAlt,
  FaFileCode,
} from "react-icons/fa";
import "./TaskAttachmentsPanel.css";

/* ─── constants ─────────────────────────────────────────────────────────── */

const PAGE_SIZE = 5;

/* ─── File-type icon map ────────────────────────────────────────────────── */

const FILE_TYPE_MAP = {
  pdf: { Icon: FaFilePdf, color: "#ef4444", bg: "#fef2f2" },
  jpg: { Icon: FaFileImage, color: "#f59e0b", bg: "#fffbeb" },
  jpeg: { Icon: FaFileImage, color: "#f59e0b", bg: "#fffbeb" },
  png: { Icon: FaFileImage, color: "#f59e0b", bg: "#fffbeb" },
  gif: { Icon: FaFileImage, color: "#f59e0b", bg: "#fffbeb" },
  webp: { Icon: FaFileImage, color: "#f59e0b", bg: "#fffbeb" },
  svg: { Icon: FaFileImage, color: "#f59e0b", bg: "#fffbeb" },
  doc: { Icon: FaFileWord, color: "#2563eb", bg: "#eff6ff" },
  docx: { Icon: FaFileWord, color: "#2563eb", bg: "#eff6ff" },
  xls: { Icon: FaFileExcel, color: "#16a34a", bg: "#f0fdf4" },
  xlsx: { Icon: FaFileExcel, color: "#16a34a", bg: "#f0fdf4" },
  zip: { Icon: FaFileArchive, color: "#7c3aed", bg: "#f5f3ff" },
  rar: { Icon: FaFileArchive, color: "#7c3aed", bg: "#f5f3ff" },
  js: { Icon: FaFileCode, color: "#ca8a04", bg: "#fefce8" },
  ts: { Icon: FaFileCode, color: "#2563eb", bg: "#eff6ff" },
  json: { Icon: FaFileCode, color: "#9333ea", bg: "#fdf4ff" },
};

export function getFileTypeCfg(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  return FILE_TYPE_MAP[ext] || { Icon: FaFileAlt, color: "#4b5563", bg: "#f9fafb" };
}

export function truncateFileName(name, max = 16) {
  if (!name || name.length <= max) return name;
  const ext = name.includes(".") ? "." + name.split(".").pop() : "";
  return name.slice(0, max - ext.length - 1) + "…" + ext;
}

/* ─── buildAttachmentList ────────────────────────────────────────────────
   Normalises a raw task object → unified { id, serverId, displayName, url }[]
   ──────────────────────────────────────────────────────────────────────── */
export function buildAttachmentList(rawTask, details) {
  const list = [];

  // Single legacy file_attachment field
  /*if (rawTask?.file_attachment) {
    const raw = rawTask.file_attachment;
    list.push({
      id:          `main-${raw}`,
      serverId:    null,
      displayName: raw.replace(/^[^-]*-[^-]*-/, ""),
      url:         `${process.env.REACT_APP_BASE_URL}uploads/${raw}`,
    });
  }*/

  // Action-level attachments array
  if (Array.isArray(details?.actions)) {
    details.actions.forEach((action) => {
      if (Array.isArray(action.attachments)) {
        action.attachments.forEach((f, i) => {
          list.push({
            id: f.id || `att-${action.id}-${i}`,
            serverId: f.id || null,
            displayName: f.originalName || f.fileName || "Attachment",
            url: f.fileName
              ? getApiUrl(`uploads/${encodeURIComponent(f.fileName)}`)
              : null,
          });
        });
      }
    });
  }

  // Task-level attachments array from `GET /api/task/:id`
  const taskAttachments = Array.isArray(rawTask?.attachments)
    ? rawTask.attachments
    : Array.isArray(details?.attachments)
      ? details.attachments
      : [];
  taskAttachments.forEach((att, i) => {
    const serverFileName = att?.file_name || att?.fileName || "";
    const originalName = att?.original_name || att?.originalName || serverFileName || "Attachment";
    const explicitUrl = att?.file_url || att?.fileUrl || null;
    list.push({
      id: att?.id || `task-att-${i}`,
      attachmentId: att?.id || null,          // UUID used by DELETE /api/task/attachment/:id
      displayName: originalName,
      url: explicitUrl || (serverFileName ? getApiUrl(`uploads/${encodeURIComponent(serverFileName)}`) : null),
    });
  });

  return list;
}

/* ─── TaskAttachmentsPanel ──────────────────────────────────────────────── */

/**
 * Props:
 *   attachments  { id, serverId, displayName, url }[]
 *                Use buildAttachmentList() to produce this from a raw task.
 *   allowDelete  boolean (optional, default true)
 */
const TaskAttachmentsPanel = ({ attachments: init = [], allowDelete = true, taskId = null }) => {
  const posthog = usePostHog();
  const [list, setList] = useState(init);
  const [visibleCount, setVisible] = useState(PAGE_SIZE);
  const [deleting, setDeleting] = useState(null); // id being deleted

  // Re-sync when parent re-passes a new list
  useEffect(() => { setList(init); }, [init]);

  const visible = list.slice(0, visibleCount);
  const remaining = list.length - visibleCount;

  const handleDelete = async (att) => {
    if (!window.confirm(`Delete "${att.displayName}"?`)) return;
    setDeleting(att.id);
    try {
      if (att.attachmentId) {
        await axiosInstance.delete(`/api/task/attachment/${att.attachmentId}`);
      }
      setList((prev) => prev.filter((a) => a.id !== att.id));
      posthog?.capture(POSTHOG_EVENTS.ATTACHMENT_REMOVED, {
        task_id: taskId,
        attachment_id: att.attachmentId || att.id,
        file_name: att.displayName,
      });
      toast.success("Attachment deleted.");
    } catch (err) {
      posthog?.capture(POSTHOG_EVENTS.ATTACHMENT_REMOVE_FAILED, {
        task_id: taskId,
        attachment_id: att.attachmentId || att.id,
        error_message: err?.message,
      });
      if (err.response?.status === 403) {
        toast.error("You don't have permission to delete this attachment.");
      } else {
        toast.error("Failed to delete attachment.");
      }
    } finally {
      setDeleting(null);
    }
  };

  if (list.length === 0) {
    return <p className="tap-empty">No attachments.</p>;
  }

  return (
    <div className="tap-root">
      {/* header: file count + collapse button */}
      <div className="tap-header-row">
        <span className="tap-count">
          {list.length} file{list.length !== 1 ? "s" : ""}
        </span>
        {visibleCount > PAGE_SIZE && (
          <button className="tap-collapse" onClick={() => setVisible(PAGE_SIZE)}>
            Collapse ↑
          </button>
        )}
      </div>

      {/* 3-column card grid */}
      <div className="tap-grid">
        {visible.map((att) => {
          const { Icon, color, bg } = getFileTypeCfg(att.displayName);
          const isDel = deleting === att.id;
          return (
            <div key={att.id} className="tap-card">
              {/* delete × */}
              {allowDelete && (
                <button
                  className="tap-delete"
                  title="Delete attachment"
                  disabled={isDel}
                  onClick={(e) => { e.stopPropagation(); handleDelete(att); }}
                >
                  {isDel ? "…" : "×"}
                </button>
              )}

              {/* icon square — click = download */}
              <a
                href={att.url}
                download={att.displayName}
                target="_blank"
                rel="noopener noreferrer"
                className="tap-icon-wrap"
                style={{ background: bg }}
                title={`Download ${att.displayName}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Icon size={18} color={color} />
              </a>

              {/* filename */}
              <Tooltip title={att.displayName} placement="bottom" arrow disableInteractive>
                <span className="tap-name">{truncateFileName(att.displayName)}</span>
              </Tooltip>
            </div>
          );
        })}
      </div>

      {/* paginate: + N more */}
      {remaining > 0 && (
        <button
          className="tap-more"
          onClick={() => setVisible((v) => v + PAGE_SIZE)}
        >
          + {remaining} more
        </button>
      )}
    </div>
  );
};

export default TaskAttachmentsPanel;
